import { useMemo, useState } from "react";

import { Column, Row, StatColumn, StatProps, Text, TextSkeleton, TextVariant, ToggleChevron } from "@bridgesplit/ui";
import {
    BsMetaUtil,
    formatLeverage,
    isValidLeverage,
    isWhirlpoolLoop,
    useUnderlyingTokenInfo,
    getHealthStatus,
    LoopExpanded,
    getLoopSlippage,
    LoopQuoteExpanded
} from "@bridgesplit/abf-react";
import { formatPercent, formatTokenAmount, Hidable } from "@bridgesplit/utils";
import { useTransactionFeeStat } from "app/components/transactions";
import { TransactionTypes } from "app/types";
import { COPY } from "app/constants";
import { PendingLeverageStatsCard } from "app/components/common";
import { Collapse, Divider } from "@mui/material";
import { SwapInstructionsResponse } from "@loopscale/pandora-types";

import { LoopMode, WindForm } from "./types";
import { HealthDetails, HealthText, LiquidationPrice } from "../../common";
import { useLoopHealth, useLoopLiquidationPrice, useManualLoopHealth, useWindExecutionDetails } from "./util";
import { useLoopContext } from "../LoopContext";
import { useWindContext } from "./WindContext";
import { ExecutionDetails } from "../execution-detail";
import { ExecutionDetailsLabels } from "../execution-detail/types";
import { WindWhirlpoolStats } from "./WindWhirlpoolStats";
import { useGetPendingLeverageStats } from "../../loan/actions/ManualIncreaseLeverage";

export default function WindStats() {
    const { loopMode } = useWindContext();
    const data = useWindSharedStatsData();

    if (!data) return null;

    if (loopMode === LoopMode.Manual) {
        return <ManualWindStats data={data} />;
    }

    return <AutoWindStats data={data} />;
}

type WindSharedStatsData = {
    variant: "body2";
    principalUsdOraclePrice: number | undefined;
    collateralUsdOraclePrice: number | undefined;
    healthDetails: HealthDetails;
    positionLtv: number | undefined;
    liquidationLtv: number | undefined;
    stats: Hidable<StatProps[]>;
    transactionFeeStat: StatProps;
    isQuoteLoading: boolean;
    isQuoteFetching: boolean;
    showExecutionDetails: boolean;
    executionDetails: ReturnType<typeof useWindExecutionDetails>["executionDetails"];
    slippagePresetsUi: number[];
    principalSymbol: string | undefined;
    principalDecimals: number | undefined;
    leverageDisplay: string | undefined;
    debtDisplay: string | undefined;
    collateralDisplay: string | undefined;
};

function useWindSharedStatsData(): WindSharedStatsData | undefined {
    const { quote, form, isQuoteFetching, isQuoteLoading, externalQuote, externalQuoteError, loopMode } =
        useWindContext();
    const { loopExpanded } = useLoopContext();
    const { oraclePrices } = useUnderlyingTokenInfo(
        loopExpanded?.principalToken.mint && loopExpanded?.collateralToken.mint
            ? [loopExpanded.principalToken.mint, loopExpanded.collateralToken.mint]
            : []
    );

    const principalOracle = oraclePrices?.get(loopExpanded?.principalToken.mint ?? "");
    const collateralOracle = oraclePrices?.get(loopExpanded?.collateralToken.mint ?? "");

    const variant = "body2" as const;

    const autoHealth = useLoopHealth();
    const manualHealth = useManualLoopHealth();
    const health = loopMode === LoopMode.Manual ? manualHealth : autoHealth;

    const transactionFeeStat = useTransactionFeeStat({
        variant: variant,
        transactionType: TransactionTypes.EarnBorrow
    });

    const liquidationDetails = useLoopLiquidationPrice();

    const { executionDetails } = useWindExecutionDetails({
        externalQuote,
        quote,
        form,
        loopExpanded,
        principalOracle,
        collateralOracle,
        labelOverrides: getLabelOverridesForWind()
    });

    const healthDetails: HealthDetails = {
        health: health?.healthRatio,
        loanHealthStatus: getHealthStatus(health?.ltv, health?.strategyLtv, health?.strategyLqt),
        ltv: health?.ltv
    };

    if (hideStats({ loopMode, form, externalQuote, quote })) return undefined;

    const leverageValue = form.multiplierWithoutDebounce ?? form.multiplier;
    const leverageDisplay =
        leverageValue !== undefined ? formatLeverage(leverageValue, leverageValue % 1 !== 0 ? 1 : 0) : undefined;
    const debtDisplay = BsMetaUtil.formatAmount(loopExpanded?.principalToken, quote?.principalAmount);
    const collateralDisplay = BsMetaUtil.formatAmount(loopExpanded?.collateralToken, quote?.leveragedCollateralAmount);

    const stats: Hidable<StatProps[]> = [
        // {
        //     caption: "Borrow APY",
        //     value: formatPercent(quote.bestQuote.apy)
        // },
        {
            value: <BorrowLiquidationPrice liquidationDetails={liquidationDetails} variant={variant} />,
            caption: COPY.LIQUIDATION_PRICE_TERM,
            tooltip: COPY.LIQUIDATION_PRICE_TOOLTIP
        },
        {
            caption: "Tick range",
            value: <ClmmStats loopExpanded={loopExpanded} variant={variant} />,
            hide: !isWhirlpoolLoop(loopExpanded)
        }
    ].filter((stat) => !stat.hide);

    // stats.push({
    //     caption: COPY.ORACLE_TERM_PLURAL,
    //     value: (
    //         <OracleLogos
    //             principalMint={loopExpanded?.loopVault.principalMint}
    //             collateralMint={loopExpanded?.loopVault.collateralMint}
    //             marketInformation={quote?.bestQuote.marketInformation}
    //             variant={variant}
    //         />
    //     )
    // });

    const showExecutionDetails = !externalQuoteError && !isWhirlpoolLoop(loopExpanded) && loopMode === LoopMode.Auto;
    const slippagePresetsUi = getLoopSlippage(loopExpanded).slippagePresetsUi;

    return {
        variant,
        healthDetails,
        positionLtv: health?.ltv,
        liquidationLtv: health?.strategyLqt,
        stats,
        transactionFeeStat,
        isQuoteLoading,
        isQuoteFetching,
        showExecutionDetails,
        executionDetails,
        slippagePresetsUi,
        principalSymbol: loopExpanded?.principalToken.symbol,
        principalDecimals: loopExpanded?.principalToken.decimals,
        principalUsdOraclePrice: principalOracle?.usdPrice,
        collateralUsdOraclePrice: collateralOracle?.usdPrice,
        leverageDisplay,
        debtDisplay,
        collateralDisplay
    };
}

function hideStats({
    loopMode,
    form,
    externalQuote,
    quote
}: {
    loopMode: LoopMode;
    form: WindForm;
    externalQuote: SwapInstructionsResponse | undefined;
    quote: LoopQuoteExpanded | null | undefined;
}): boolean {
    if (loopMode === LoopMode.Manual) {
        return !form.collateralAmount || !form.principalAmount;
    }

    return !form.inputAmount || !isValidLeverage(form.multiplier) || !externalQuote || !quote;
}

function AutoWindStats({ data }: { data: WindSharedStatsData }) {
    return (
        <Column
            spacing={0.5}
            sx={{
                opacity: data.isQuoteFetching && !data.isQuoteLoading ? 0.5 : 1,
                transition: "opacity 0.2s ease"
            }}
        >
            <WindLeverageDropdown
                variant={data.variant}
                loading={data.isQuoteLoading}
                leverage={data.leverageDisplay}
                debt={data.debtDisplay}
                collateral={data.collateralDisplay}
            />
            <WindHealthDropdown
                variant={data.variant}
                loading={data.isQuoteLoading}
                healthDetails={data.healthDetails}
                positionLtv={data.positionLtv}
                liquidationLtv={data.liquidationLtv}
            />
            <StatColumn variant={data.variant} loading={data.isQuoteLoading} stats={data.stats} />
            {data.showExecutionDetails && (
                <ExecutionDetails
                    executionDetails={data.executionDetails}
                    loading={data.isQuoteLoading}
                    textVariant="body2"
                    slippagePresetsUi={data.slippagePresetsUi}
                />
            )}
            <StatColumn variant={data.variant} loading={data.isQuoteLoading} stats={[data.transactionFeeStat]} />
        </Column>
    );
}

function WindLeverageDropdown({
    variant,
    loading,
    leverage,
    debt,
    collateral
}: {
    variant: TextVariant;
    loading: boolean;
    leverage: string | undefined;
    debt: string | undefined;
    collateral: string | undefined;
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const stats = useMemo<StatProps[]>(
        () => [
            { caption: "Debt", value: debt },
            { caption: "Collateral", value: collateral }
        ],
        [debt, collateral]
    );

    return (
        <Column>
            <Row sx={{ cursor: "pointer" }} spaceBetween>
                <Text color="caption" variant={variant} loading={loading}>
                    Leverage
                </Text>
                <Row spacing={1}>
                    <Text variant={variant} loading={loading} color="body">
                        {leverage}
                    </Text>
                    <ToggleChevron
                        onClick={() => setIsExpanded(!isExpanded)}
                        textVariant="body1"
                        sx={{ p: 0 }}
                        expanded={isExpanded}
                    />
                </Row>
            </Row>
            <Collapse in={isExpanded}>
                <Column sx={{ mt: 1 }} spacing={1}>
                    <Divider />
                    <StatColumn loading={loading} variant={variant} spacing={0.5} stats={stats} />
                </Column>
            </Collapse>
        </Column>
    );
}

function ManualWindStats({ data }: { data: WindSharedStatsData }) {
    const { form } = useWindContext();
    const { loopExpanded } = useLoopContext();
    const principalInputBorrowAmount = form.principalAmount;
    const collateralInputDepositAmount = form.collateralAmount;
    const shouldShowPendingLeverage = (principalInputBorrowAmount ?? 0) > 0;
    const manualInputStats = useMemo<StatProps[]>(
        () => [
            {
                caption: "Collateral",
                value: BsMetaUtil.formatAmount(loopExpanded?.collateralToken, collateralInputDepositAmount)
            },
            {
                caption: "Debt",
                value: BsMetaUtil.formatAmount(loopExpanded?.principalToken, principalInputBorrowAmount)
            }
        ],
        [
            loopExpanded?.collateralToken,
            loopExpanded?.principalToken,
            collateralInputDepositAmount,
            principalInputBorrowAmount
        ]
    );

    const pendingLeverageStats = useGetPendingLeverageStats({
        originalPrincipalAmount: 0,
        originalCollateralAmount: collateralInputDepositAmount,
        collateralInputDepositAmount: 0,
        principalInputBorrowAmount: principalInputBorrowAmount,
        healthBeforeSwap: data.healthDetails.health,
        ltvBeforeSwap: data.positionLtv,
        liquidationThreshold: data.liquidationLtv,
        principalUsdOraclePrice: data.principalUsdOraclePrice,
        collateralUsdOraclePrice: data.collateralUsdOraclePrice,
        variant: data.variant
    });

    const pendingLeverageHelpText = `Expected leverage after you swap the borrowed ${formatTokenAmount(
        principalInputBorrowAmount,
        {
            symbol: data.principalSymbol,
            decimals: data.principalDecimals
        }
    )} and deposit collateral`;

    return (
        <Column
            spacing={1.5}
            sx={{
                opacity: data.isQuoteFetching && !data.isQuoteLoading ? 0.5 : 1,
                transition: "opacity 0.2s ease"
            }}
        >
            <Column spacing={0.5}>
                <StatColumn variant={data.variant} loading={data.isQuoteLoading} stats={manualInputStats} />
                <WindHealthDropdown
                    variant={data.variant}
                    loading={data.isQuoteLoading}
                    healthDetails={data.healthDetails}
                    positionLtv={data.positionLtv}
                    liquidationLtv={data.liquidationLtv}
                />
                <StatColumn variant={data.variant} loading={data.isQuoteLoading} stats={data.stats} />
                <StatColumn variant={data.variant} loading={data.isQuoteLoading} stats={[data.transactionFeeStat]} />
            </Column>
            {shouldShowPendingLeverage && (
                <PendingLeverageStatsCard
                    variant={data.variant}
                    title="New pending leverage"
                    helpText={pendingLeverageHelpText}
                    stats={pendingLeverageStats}
                    loading={data.isQuoteLoading}
                />
            )}
        </Column>
    );
}

function WindHealthDropdown({
    variant,
    loading,
    healthDetails,
    positionLtv,
    liquidationLtv
}: {
    variant: TextVariant;
    loading: boolean;
    healthDetails: HealthDetails;
    positionLtv: number | undefined;
    liquidationLtv: number | undefined;
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const stats = useMemo<StatProps[]>(
        () => [
            {
                caption: "Position LTV",
                value: formatPercent(positionLtv)
            },
            {
                caption: "Liquidation LTV",
                value: formatPercent(liquidationLtv)
            }
        ],
        [positionLtv, liquidationLtv]
    );

    return (
        <Column>
            <Row sx={{ cursor: "pointer" }} spaceBetween>
                <Text color="caption" variant={variant} loading={loading}>
                    Health
                </Text>
                <Row spacing={1}>
                    <HealthText variant={variant} {...healthDetails} loading={loading} />
                    <ToggleChevron
                        onClick={() => setIsExpanded(!isExpanded)}
                        textVariant="body1"
                        sx={{ p: 0 }}
                        expanded={isExpanded}
                    />
                </Row>
            </Row>
            <Collapse in={isExpanded}>
                <Column sx={{ mt: 1 }} spacing={1}>
                    <Divider />
                    <StatColumn loading={loading} variant={variant} spacing={0.5} stats={stats} />
                </Column>
            </Collapse>
        </Column>
    );
}

function BorrowLiquidationPrice({
    variant,
    liquidationDetails
}: {
    variant: TextVariant;
    liquidationDetails: ReturnType<typeof useLoopLiquidationPrice>;
}) {
    const { loopExpanded } = useLoopContext();

    const { liquidationPrice, percentChange } = liquidationDetails;

    if (!liquidationPrice) return <TextSkeleton variant={variant} />;

    return (
        <LiquidationPrice
            variant={variant}
            principal={loopExpanded?.principalToken}
            collateral={loopExpanded?.collateralToken}
            liquidationPrice={liquidationPrice}
            percentChange={percentChange}
        />
    );
}

function getLabelOverridesForWind(): Partial<ExecutionDetailsLabels> {
    return {
        executionImpact: {
            caption: "Execution impact",
            tooltip:
                "Combined worst-case effect of slippage, fees, price impact, and market price when opening a position"
        },
        fairMarketValue: {
            caption: "Fair market value",
            tooltip: "Your contributed collateral plus borrowed principal valued in collateral at oracle prices"
        },
        oracleSpread: {
            caption: "Oracle spread",
            tooltip:
                "Difference between the oracle-estimated collateral for your borrowed principal and the actual collateral received in the market"
        },
        receiveOnClose: {
            caption: "",
            hide: true,
            tooltip: undefined
        }
    };
}

function ClmmStats({ loopExpanded, variant }: { loopExpanded: LoopExpanded | undefined; variant: TextVariant }) {
    if (!loopExpanded || !isWhirlpoolLoop(loopExpanded)) return null;

    return <WindWhirlpoolStats loopExpanded={loopExpanded} variant={variant} />;
}
